/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, BrowserRouter, Navigate, useNavigate, useLocation } from 'react-router-dom';
import TopBarProgress from 'react-topbar-progress-indicator';
import { PrivateRoutes } from './PrivateRoutes';
import { ErrorsPage } from '../modules/errors/ErrorsPage';
import { Logout, AuthPage, useAuth } from '../modules/auth';
import { App } from '../App';
import { isExtensionImpersonationSession } from './extensionImpersonation';

const Dashboards = lazy(() => import('../modules/apps/Dashboards/Dashboards'));
const ContactMatcher = lazy(() => import('../modules/apps/ContactMatcher/ContactMatcher'));
const CDRSTABLE = lazy(() => import('../modules/apps/cdrTable/CDRSTABLE'));
const DashboardsSelected = lazy(() => import('../modules/apps/Dashboards/DashboardsSelected'));
const CallMonitoring = lazy(() => import('../modules/apps/CallMonitoring2/CallMonitoring'));
const CDRS = lazy(() => import('../modules/apps/cdr-custom/CDRS'));
const SettingsTab = lazy(() => import('../modules/apps/settings-custom/AppSettings'));
const AccountSettings = lazy(() => import('../modules/apps/settings-custom/Account'));
const CallControlSettings = lazy(() => import('../modules/apps/settings-custom/CallControl'));
const CallGroupsSettings = lazy(() => import('../modules/apps/settings-custom/CallGroups'));
const BlockedNumbersSettings = lazy(() => import('../modules/apps/settings-custom/BlockedNumbers'));
const SchedulesSettings = lazy(() => import('../modules/apps/settings-custom/Schedules'));
const VoicemailSettings = lazy(() => import('../modules/apps/settings-custom/Voicemail'));
const BlfSettings = lazy(() => import('../modules/apps/settings-custom/Blfs'));
const QRGenerator = lazy(() => import('../modules/apps/settings-custom/QrGenerator'));

const SuspenseRoute = ({ children }) => (
  <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
);

/**
 * Wrappers to support navigation/route props in class components (React Router v6)
 */
const CDRSWrapper = (props) => {
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <CDRS {...props} navigate={navigate} />
    </SuspenseRoute>
  );
};

const QRGeneratorWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <QRGenerator {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const AccountSettingsWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <AccountSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const CallControlWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <CallControlSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const CallGroupsWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <CallGroupsSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const BlockedNumbersWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <BlockedNumbersSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const SchedulesWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <SchedulesSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const VoicemailWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <VoicemailSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

const BlfWrapper = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SuspenseRoute>
      <BlfSettings {...props} location={location} navigate={navigate} />
    </SuspenseRoute>
  );
};

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

const AppRoutes = () => {
  const { currentUser } = useAuth();

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='dashboards' element={<SuspenseRoute><Dashboards /></SuspenseRoute>} />
          <Route path='cdrs' element={<CDRSWrapper />} />
          <Route path='/cdrs-table' element={<SuspenseRoute><CDRSTABLE /></SuspenseRoute>} />
          <Route path='/getQR' element={<QRGeneratorWrapper />} />
          <Route path='/settings_tab/appSettings' element={<SuspenseRoute><SettingsTab /></SuspenseRoute>} />
          <Route path='/settings_tab/accountSettings' element={<AccountSettingsWrapper />} />
          <Route path='/settings_tab/callControlSettings' element={<CallControlWrapper />} />
          <Route path='/settings_tab/callGroupsSettings' element={<CallGroupsWrapper />} />
          <Route path='/settings_tab/blockedNumbersSettings' element={<BlockedNumbersWrapper />} />
          <Route path='/settings_tab/schedulesSettings' element={<SchedulesWrapper />} />
          <Route path='/settings_tab/voicemailSettings' element={<VoicemailWrapper />} />
          <Route path='/settings_tab/blfSettings' element={<BlfWrapper />} />

          <Route path='contactTest' element={<SuspenseRoute><ContactMatcher /></SuspenseRoute>} />
          <Route path='dashboardselected/:type/:extension' element={<SuspenseRoute><DashboardsSelected /></SuspenseRoute>} />
          <Route
            path='callMonitoring'
            element={
              (() => {
                const userType = localStorage.getItem('user_type');
                if (userType !== 'User' || isExtensionImpersonationSession()) {
                  return (
                    <SuspenseRoute>
                      <CallMonitoring />
                    </SuspenseRoute>
                  );
                }
                return <Navigate to='/error/404' />;
              })()
            }
          />
          <Route path='logout' element={<Logout />} />

          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='profile' />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export { AppRoutes };
